import React, { useState, useEffect } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { Link } from 'react-router-dom';
import { app } from '../../firebase';
import { toast } from 'react-toastify';
import {
  FaPaperPlane,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaEye,
  FaBriefcase,
  FaCalendarAlt,
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaClipboardList
} from 'react-icons/fa';

const InvitedCandidates = () => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, pending, applied, declined
  const [expandedRow, setExpandedRow] = useState(null);
  const [applicationData, setApplicationData] = useState({}); // { [invitationId]: { answers, questions, loading, error } }

  const auth = getAuth(app);
  const db = getDatabase(app);

  useEffect(() => {
    const fetchInvitations = async () => {
      if (!auth.currentUser) return;

      try {
        const invitationsRef = ref(db, `invitations/${auth.currentUser.uid}`);
        const snapshot = await get(invitationsRef);

        if (snapshot.exists()) {
          const invitationsData = snapshot.val();
          const invitationsArray = Object.keys(invitationsData).map(key => ({
            id: key,
            ...invitationsData[key]
          }));

          // Sort by date sent (most recent first)
          invitationsArray.sort((a, b) => new Date(b.sentAt) - new Date(a.sentAt));

          setInvitations(invitationsArray);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching invitations:', error);
        toast.error('Error loading invitations');
        setLoading(false);
      }
    };

    fetchInvitations();
  }, [auth.currentUser, db]);

  // Toggle answers panel — fetches lazily on first open
  const handleToggleAnswers = async (invitation) => {
    if (expandedRow === invitation.id) {
      setExpandedRow(null);
      return;
    }
    setExpandedRow(invitation.id);

    if (applicationData[invitation.id]) return; // already loaded

    setApplicationData(prev => ({ ...prev, [invitation.id]: { loading: true } }));
    try {
      const uid = auth.currentUser.uid;
      let [appSnapshot, jobSnapshot] = await Promise.all([
        get(ref(db, `jobs/${uid}/${invitation.jobId}/applications/${invitation.candidateId}`)),
        get(ref(db, `jobs/${uid}/${invitation.jobId}`))
      ]);

      // Fall back to expired-jobs/ if the listing has since expired
      if (!jobSnapshot.exists()) {
        [appSnapshot, jobSnapshot] = await Promise.all([
          get(ref(db, `expired-jobs/${uid}/${invitation.jobId}/applications/${invitation.candidateId}`)),
          get(ref(db, `expired-jobs/${uid}/${invitation.jobId}`))
        ]);
      }

      const answers = appSnapshot.exists() ? (appSnapshot.val().answers || null) : null;
      const questions = jobSnapshot.exists() ? (jobSnapshot.val().questions || null) : null;

      setApplicationData(prev => ({ ...prev, [invitation.id]: { answers, questions, loading: false } }));
    } catch (error) {
      console.error('Error fetching application answers:', error);
      setApplicationData(prev => ({ ...prev, [invitation.id]: { error: true, loading: false } }));
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: {
        icon: FaClock,
        text: 'Response Pending',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        iconColor: 'text-yellow-600'
      },
      applied: {
        icon: FaCheckCircle,
        text: 'Applied',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        iconColor: 'text-green-600'
      },
      declined: {
        icon: FaTimesCircle,
        text: 'Declined Invitation',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        iconColor: 'text-red-600'
      }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bgColor} ${config.textColor}`}>
        <Icon className={`mr-1.5 ${config.iconColor}`} size={14} />
        {config.text}
      </span>
    );
  };

  // Filter invitations
  const filteredInvitations = invitations
    .filter(invitation => {
      // Filter by status
      if (filterStatus !== 'all' && invitation.status !== filterStatus) {
        return false;
      }

      // Filter by search term
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          invitation.candidateName?.toLowerCase().includes(searchLower) ||
          invitation.jobTitle?.toLowerCase().includes(searchLower) ||
          invitation.companyName?.toLowerCase().includes(searchLower)
        );
      }

      return true;
    });

  // Get statistics
  const stats = {
    total: invitations.length,
    pending: invitations.filter(inv => inv.status === 'pending').length,
    applied: invitations.filter(inv => inv.status === 'applied').length,
    declined: invitations.filter(inv => inv.status === 'declined').length
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-950"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-blue-950 mb-2">Invited Candidates</h1>
        <p className="text-gray-600">Track candidates you've invited to apply for your job openings</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Invitations</p>
              <p className="text-2xl font-bold text-blue-950">{stats.total}</p>
            </div>
            <FaPaperPlane className="text-blue-500" size={32} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Response Pending</p>
              <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
            </div>
            <FaClock className="text-yellow-500" size={32} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Applied</p>
              <p className="text-2xl font-bold text-green-700">{stats.applied}</p>
            </div>
            <FaCheckCircle className="text-green-500" size={32} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Declined</p>
              <p className="text-2xl font-bold text-red-700">{stats.declined}</p>
            </div>
            <FaTimesCircle className="text-red-500" size={32} />
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by candidate name, job title, or company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
              <FaSearch className="absolute left-3 top-3 text-gray-400" />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Status Filter */}
          <div className="md:w-64">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
            >
              <option value="all">All Status ({stats.total})</option>
              <option value="pending">Response Pending ({stats.pending})</option>
              <option value="applied">Applied ({stats.applied})</option>
              <option value="declined">Declined ({stats.declined})</option>
            </select>
          </div>
        </div>
      </div>

      {/* Invitations List */}
      {filteredInvitations.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <FaPaperPlane className="mx-auto h-16 w-16 text-gray-300 mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">
            {invitations.length === 0 ? 'No Invitations Sent Yet' : 'No Results Found'}
          </h3>
          <p className="text-gray-500 mb-6">
            {invitations.length === 0
              ? "Start inviting candidates to apply for your job openings from the All Candidates page."
              : "No invitations match your search criteria. Try adjusting your filters."}
          </p>
          {invitations.length === 0 && (
            <Link
              to="/employer/resumes"
              className="inline-flex items-center px-6 py-3 bg-blue-950 text-white rounded-md hover:bg-blue-800 transition-colors"
            >
              Browse Candidates
            </Link>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100 border-b-2 border-gray-300">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Candidate</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Job Position</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Date Invited</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredInvitations.map((invitation) => (
                  <React.Fragment key={invitation.id}>
                    <tr className="hover:bg-gray-50 transition-colors">
                      {/* Candidate */}
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-semibold text-blue-950">{invitation.candidateName}</p>
                          <p className="text-sm text-gray-500">{invitation.candidateEmail}</p>
                        </div>
                      </td>

                      {/* Job Position */}
                      <td className="px-6 py-4">
                        <div className="flex items-start">
                          <FaBriefcase className="mr-2 mt-1 text-blue-950 flex-shrink-0" size={16} />
                          <div>
                            <p className="font-medium text-gray-900">{invitation.jobTitle}</p>
                            <p className="text-sm text-gray-500">{invitation.companyName}</p>
                          </div>
                        </div>
                      </td>

                      {/* Date Invited */}
                      <td className="px-6 py-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <FaCalendarAlt className="mr-2 text-gray-400" size={14} />
                          {formatDate(invitation.sentAt)}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4">
                        {getStatusBadge(invitation.status)}
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Link
                            to={`/employer/resumes/${invitation.candidateId}`}
                            className="inline-flex items-center px-3 py-2 bg-blue-950 text-white text-sm rounded-md hover:bg-blue-800 transition-colors"
                            title="View Candidate Profile"
                          >
                            <FaEye className="mr-1" size={14} />
                            View Profile
                          </Link>
                          {invitation.status === 'applied' && (
                            <>
                              <Link
                                to="/employer/applications"
                                className="inline-flex items-center px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors"
                                title="View Applications"
                              >
                                View Application
                              </Link>
                              <button
                                onClick={() => handleToggleAnswers(invitation)}
                                className="inline-flex items-center px-3 py-2 bg-purple-100 text-purple-700 text-sm rounded-md hover:bg-purple-200 transition-colors"
                                title="View Application Answers"
                              >
                                <FaClipboardList className="mr-1" size={14} />
                                Answers
                                {expandedRow === invitation.id
                                  ? <FaChevronUp className="ml-1" size={12} />
                                  : <FaChevronDown className="ml-1" size={12} />}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Expandable answers row */}
                    {expandedRow === invitation.id && (
                      <tr>
                        <td colSpan={5} className="px-6 py-4 bg-purple-50 border-b border-purple-100">
                          {applicationData[invitation.id]?.loading ? (
                            <div className="flex items-center text-purple-700 text-sm">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-700 mr-2"></div>
                              Loading answers...
                            </div>
                          ) : applicationData[invitation.id]?.error ? (
                            <p className="text-red-600 text-sm">Failed to load answers.</p>
                          ) : applicationData[invitation.id]?.answers ? (
                            <div>
                              <h4 className="font-semibold text-purple-900 mb-3 flex items-center">
                                <FaClipboardList className="mr-2" size={14} />
                                Application Responses — {invitation.candidateName}
                              </h4>
                              <div className="space-y-3">
                                {Object.entries(applicationData[invitation.id].answers).map(([questionId, answer], index) => {
                                  const questions = applicationData[invitation.id].questions;
                                  const questionObj = questions
                                    ? Object.values(questions).find(q => q.id === questionId)
                                    : null;
                                  return (
                                    <div key={questionId} className="bg-white rounded-lg p-3 border border-purple-200">
                                      <p className="text-sm font-medium text-gray-700 mb-1">
                                        Q{index + 1}: {questionObj?.question || `Question ${index + 1}`}
                                      </p>
                                      <p className="text-sm text-gray-900">{answer || <span className="text-gray-400 italic">No answer provided</span>}</p>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ) : (
                            <p className="text-gray-500 text-sm italic">No application questions were answered.</p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Results Summary */}
      {filteredInvitations.length > 0 && (
        <div className="mt-4 text-sm text-gray-600 text-center">
          Showing {filteredInvitations.length} of {invitations.length} invitation{invitations.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};

export default InvitedCandidates;
